import { FC, useEffect, useState } from 'react'
import { IArpMedia } from '../../../../features/arp-registry/ArpMedia'
import { Column } from '../../core/Components'
import { Title } from '../../core/Title'
import DNATable from './DNATable'
import DNATabs from './DNATabs'

export interface DNATabsProps {
  samplesUsed?: IArpMedia[]
  stemsUsed?: IArpMedia[]
  remixes?: IArpMedia[]
  stemsFeaturedIn?: IArpMedia[]
  songsFeaturedIn?: IArpMedia[]
  clipArpIndexes?: number[] // an array containing the arp indexes of all media that's used as a clips
}

interface Props extends DNATabsProps {
  title: string
}

export const DNATabLabels = {
  SAMPLES_USED: 'samples_used',
  STEMS_USED: 'stems_used',
  REMIXES: 'remixes',
  STEMS_FEATURED_IN: 'stems_featured_in',
  SONGS_FEATURED_IN: 'songs_featured_in'
}

const useDetailPageDNA = (
  samplesUsed: IArpMedia[],
  stemsUsed: IArpMedia[],
  remixes: IArpMedia[],
  stemsFeaturedIn: IArpMedia[],
  songsFeaturedIn: IArpMedia[]
) => {
  const [selectedTab, setSelectedTab] = useState<string>('')
  const [tabs, setTabs] = useState<{ title: string; value: string }[]>([])
  useEffect(() => {
    const tempTabs = []
    if (samplesUsed && samplesUsed.length > 0)
      tempTabs.push({ title: 'Samples Used', value: DNATabLabels.SAMPLES_USED })
    if (songsFeaturedIn && songsFeaturedIn.length > 0)
      tempTabs.push({ title: 'Songs Featured in', value: DNATabLabels.SONGS_FEATURED_IN })
    if (stemsFeaturedIn && stemsFeaturedIn.length > 0)
      tempTabs.push({ title: 'Stems Featured in', value: DNATabLabels.STEMS_FEATURED_IN })
    if (stemsUsed && stemsUsed.length > 0)
      tempTabs.push({ title: 'Stems Used', value: DNATabLabels.STEMS_USED })
    if (remixes && remixes.length > 0)
      tempTabs.push({ title: 'Remixes', value: DNATabLabels.REMIXES })
    setTabs(tempTabs)
  }, [songsFeaturedIn, stemsFeaturedIn, samplesUsed, stemsUsed, remixes])

  useEffect(() => {
    const value = tabs[0] ? tabs[0].value : ''
    setSelectedTab(value)
  }, [tabs])

  //  if the selected tab isn't valid, set it to a valid one
  if (tabs.length > 0 && tabs.filter((item) => item.value === selectedTab).length === 0)
    setSelectedTab(tabs[0].value)

  return { selectedTab, setSelectedTab, tabs }
}

const DetailPageDNA: FC<Props> = ({
  title,
  samplesUsed,
  stemsUsed,
  remixes,
  stemsFeaturedIn,
  songsFeaturedIn,
  clipArpIndexes
}) => {
  const { selectedTab, setSelectedTab, tabs } = useDetailPageDNA(
    samplesUsed,
    stemsUsed,
    remixes,
    stemsFeaturedIn,
    songsFeaturedIn
  )

  return (
    <Column
      css={{
        padding: '50px 15px',
        '@mobile': { paddingLeft: '30px', paddingRight: '30px' },
        '@tablet': {
          padding: '70px 140px'
        },
        position: 'relative'
      }}
    >
      <Title css={{ marginBottom: '30px' }}>{`${title} DNA`}</Title>
      <DNATabs tabs={tabs} selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
      <DNATable
        selectedTab={selectedTab}
        samplesUsed={samplesUsed}
        stemsUsed={stemsUsed}
        remixes={remixes}
        stemsFeaturedIn={stemsFeaturedIn}
        songsFeaturedIn={songsFeaturedIn}
        clipArpIndexes={clipArpIndexes}
      />
    </Column>
  )
}

export default DetailPageDNA
